import React, { useState } from "react";

/**
 * Ottelun pelaajien korjaus (ottelukortti, GamesSection).
 *
 * Arvonta tai käsin lisätty ottelu voi mennä väärin — esimerkiksi
 * kierroksella pelasivat eri parit kuin sovellus arpoi. Ennen tätä
 * ottelusta pystyi muuttamaan vain lukemat, ja väärät pelaajat jäivät
 * pysyviksi myös päätetyssä illassa.
 *
 * Joukkueessa 1–2 pelaajaa (kaksinpeli sallittu, kuten Lisää ottelu).
 * Sama pelaaja ei voi olla kahdessa paikassa.
 */
export function validTeams(team1: string[], team2: string[]): boolean {
  const all = [...team1, ...team2];
  return team1.length >= 1 && team2.length >= 1 && team1.length <= 2 && team2.length <= 2 &&
    all.every(n => n.trim() !== "") &&
    new Set(all.map(n => n.trim().toLowerCase())).size === all.length;
}

export default function MatchPlayersEditor({ names, team1, team2, finished, lang, onSave, onCancel }: {
  names: string[];
  team1: string[];
  team2: string[];
  finished?: boolean;
  lang: "fi" | "en";
  onSave: (team1: string[], team2: string[]) => Promise<void>;
  onCancel: () => void;
}) {
  const fi = lang !== "en";
  const pad = (t: string[]) => [t[0] ?? "", t[1] ?? ""];
  const [slots, setSlots] = useState<string[]>([...pad(team1), ...pad(team2)]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Nykyiset nimet mukaan, vaikka pelaaja olisi poistunut osallistujista.
  const options = Array.from(new Set([...names, ...team1, ...team2].filter(Boolean)))
    .sort((a, b) => a.localeCompare(b, "fi"));

  const t1 = [slots[0], slots[1]].filter(Boolean);
  const t2 = [slots[2], slots[3]].filter(Boolean);
  const ok = validTeams(t1, t2);

  async function save() {
    if (!ok || saving) return;
    setSaving(true);
    setError("");
    try {
      await onSave(t1, t2);
    } catch (err) {
      console.error("MatchPlayersEditor", err);
      setError(fi ? "Tallennus epäonnistui. Tarkista yhteys." : "Could not save. Check your connection.");
      setSaving(false);
    }
  }

  const sel = (i: number, optional: boolean) => (
    <select
      value={slots[i]}
      onChange={e => setSlots(s => s.map((v, j) => (j === i ? e.target.value : v)))}
      aria-label={fi ? `Pelaaja ${i + 1}` : `Player ${i + 1}`}
      style={{
        width: "100%", padding: "6px 8px", borderRadius: 8, fontSize: 13,
        border: "1px solid var(--sl-border)", background: "var(--sl-card)", color: "var(--sl-text)",
        appearance: "auto" as React.CSSProperties["appearance"],
      }}
    >
      <option value="">{optional ? (fi ? "— ei toista —" : "— none —") : (fi ? "Valitse…" : "Choose…")}</option>
      {options.map(n => <option key={n} value={n}>{n}</option>)}
    </select>
  );

  return (
    <div style={{ marginTop: 8, padding: "10px", borderRadius: 8, background: "var(--sl-card)", border: "1px solid var(--sl-border)" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, alignItems: "center" }}>
        <div style={{ display: "grid", gap: 4 }}>{sel(0, false)}{sel(1, true)}</div>
        <span style={{ fontSize: 12, fontWeight: 700, color: "var(--sl-muted)" }}>vs</span>
        <div style={{ display: "grid", gap: 4 }}>{sel(2, false)}{sel(3, true)}</div>
      </div>
      {!ok && (
        <div style={{ fontSize: 11, color: "#b45309", marginTop: 6 }}>
          {fi ? "Kummassakin joukkueessa 1–2 pelaajaa, eikä sama pelaaja kahdesti." : "Each team needs 1–2 players, no player twice."}
        </div>
      )}
      {finished && (
        <div style={{ fontSize: 11, color: "var(--sl-muted)", marginTop: 6, lineHeight: 1.45 }}>
          {fi
            ? "Tapahtuma on päätetty: tallennuksen jälkeen paina Laske sijoitukset uudelleen, jos se ilmestyy. Pelaajien kokonaisvoitot eivät päivity."
            : "The event has ended: after saving, tap Recalculate placings if it appears. Players' overall win totals are not updated."}
        </div>
      )}
      {error && <div style={{ fontSize: 12, color: "#dc2626", marginTop: 6 }}>{error}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button onClick={save} disabled={!ok || saving} style={{
          flex: 1, border: "none", borderRadius: 8, padding: "8px 0", fontSize: 13, fontWeight: 700,
          background: "var(--sl-primary)", color: "#fff", opacity: !ok || saving ? 0.5 : 1, cursor: "pointer",
        }}>
          {saving ? (fi ? "Tallennetaan…" : "Saving…") : (fi ? "Tallenna pelaajat" : "Save players")}
        </button>
        <button onClick={onCancel} disabled={saving} style={{
          flex: 1, border: "none", borderRadius: 8, padding: "8px 0", fontSize: 13, fontWeight: 700,
          background: "var(--sl-soft)", color: "var(--sl-text)", cursor: "pointer",
        }}>
          {fi ? "Peruuta" : "Cancel"}
        </button>
      </div>
    </div>
  );
}
